import React, { ReactElement } from 'react';
import classNames from 'classnames/bind';
import styles from 'sections/Resources/BillOfLading/components/SectionTypes/SectionTypes.module.scss';

const cn = classNames.bind(styles);

const SectionTypes = (): ReactElement => {
  return (
    <section className={cn('types')}>
      <div className={cn('container')}>

        <h2 className={cn('types__heading')}>Types of Bills of Lading</h2>
        <p className={cn('types__heading-description')}>
          There are many different types of bills of lading, each suited to specific shipping scenarios and
          requirements.
        </p>
        <p className={cn('types__heading-description')}>
          A Bill of Lading (BoL) is a critical document in the shipping and logistics industry, serving
          multiple roles: a receipt for goods shipped, a document of title, and a contract between the
          shipper and the carrier. Traditionally, a paper bill of lading has been used, but electronic
          alternatives are becoming more common. Bills of lading are especially important in sea
          transport, where goods are shipped over long distances and require detailed documentation.
          Essentially, it is the paperwork that documents the safe and legal transport of goods from one
          place to another.
        </p>

        <h3 className={cn('types__purpose-heading')}>Purpose and Importance</h3>
        <p className={cn('types__purpose-description')}>
          Understanding the various types of Bills of Lading is crucial for efficient and compliant logistics
          management. Each type has its specific applications and legal implications, which can affect how goods are
          transported and received.
        </p>

        <ul className={cn('types__benefits')}>
          <li className={cn('types__benefit')}>
            <div className={cn('types__benefit-icon-box')}>
              <img
                src="/images/pages/Resources/BillOfLading/icon-file.png"
                srcSet="/images/pages/Resources/BillOfLading/icon-file.png 1x,
                    /images/pages/Resources/BillOfLading/icon-file@2x.png 2x"
                alt="bill"
              />
            </div>
            <h4 className={cn('types__benefit-heading')}>Straight Bill of Lading</h4>
            <p className={cn('types__benefit-description')}>
              The Straight Bill of Lading is non-negotiable and consigned to a specific recipient, often used
              for goods sold on a fixed basis where the consignee is known. The designated recipient must
              be the one to claim the goods, and this type of BoL cannot be transferred to another party. It
              provides a straightforward way to document the transport of goods, ensuring that they are
              delivered only to the intended recipient.
            </p>
          </li>

          <li className={cn('types__benefit')}>
            <div className={cn('types__benefit-icon-box')}>
              <img
                src="/images/pages/Resources/BillOfLading/icon-file.png"
                srcSet="/images/pages/Resources/BillOfLading/icon-file.png 1x,
                    /images/pages/Resources/BillOfLading/icon-file@2x.png 2x"
                alt="bill"
              />
            </div>
            <h4 className={cn('types__benefit-heading')}>Order Bill of Lading</h4>
            <p className={cn('types__benefit-description')}>
              The Order Bill of Lading is negotiable and can be endorsed and transferred to a third party,
              making it suitable for international trade where the goods may be sold multiple times during
              transit. This flexibility allows the transfer of ownership through endorsements, which can
              facilitate trade financing and reduce the risk for sellers and buyers. The Order BoL can
              significantly streamline the logistics of high-value or frequently traded goods.
            </p>
          </li>

          <li className={cn('types__benefit')}>
            <div className={cn('types__benefit-icon-box')}>
              <img
                src="/images/pages/Resources/BillOfLading/icon-file.png"
                srcSet="/images/pages/Resources/BillOfLading/icon-file.png 1x,
                    /images/pages/Resources/BillOfLading/icon-file@2x.png 2x"
                alt="bill"
              />
            </div>
            <h4 className={cn('types__benefit-heading')}>Bearer Bill of Lading</h4>
            <p className={cn('types__benefit-description')}>
              A Bearer Bill of Lading allows the holder of the document to claim the goods, regardless of
              whether they are named on the document or not. This type of BoL is less common due to
              higher risks associated with theft and fraud, as anyone in possession of the document can
              claim the cargo. However, it can be useful in certain situations where quick and flexible
              transfer of goods is necessary.
            </p>
          </li>

          <li className={cn('types__benefit')}>
            <div className={cn('types__benefit-icon-box')}>
              <img
                src="/images/pages/Resources/BillOfLading/icon-file.png"
                srcSet="/images/pages/Resources/BillOfLading/icon-file.png 1x,
                    /images/pages/Resources/BillOfLading/icon-file@2x.png 2x"
                alt="bill"
              />
            </div>
            <h4 className={cn('types__benefit-heading')}>Sea Waybill</h4>
            <p className={cn('types__benefit-description')}>
              The Sea Waybill is a transport document that, unlike a traditional Bill of Lading, does not serve
              as a document of title and does not need to be presented to claim the goods. This simplifies
              the logistics process when no transfer of ownership is involved during the shipment. The Sea
              Waybill is particularly useful for intra-company shipments or where the consignee is known
              and trusted.
            </p>
          </li>
        </ul>

        <div className={cn('types__summary')}>
          <p>
            Each type of Bill of Lading serves different purposes and has specific legal implications. Selecting the
            appropriate BoL is critical to ensuring compliance and smooth logistics operations, as it affects the
            transfer of ownership, risk management, and legal responsibilities.
          </p>
          <p>
            Each type of Bill of Lading serves different purposes and comes with specific legal implications. Choosing
            the right type ensures compliance and smooth logistics operations.
          </p>
        </div>
      </div>
    </section>
  );
};

export default SectionTypes;